/**
 * KnowledgeDetails
 *
 * Side panel for viewing domain knowledge details and taking actions.
 *
 * Part of Story 30: Domain Knowledge Refinement System
 *
 * Date: December 18, 2025
 */

import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { toast } from '@/components/ui/use-toast';
import {
  BookOpen,
  Wrench,
  MessageSquare,
  Award,
  CheckCircle2,
  FlaskConical,
  AlertTriangle,
  Clock,
  ThumbsUp,
  ThumbsDown,
  Play,
  RotateCcw,
  ExternalLink,
  Loader2,
  Zap,
  Package,
  ArrowRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  DomainKnowledge,
  KnowledgeType,
  KnowledgeStatus,
  updateKnowledgeStatus,
  executeResearch,
  retryResearch,
  propagateStaleness,
} from '@/services/domainKnowledgeService';

interface KnowledgeDetailsProps {
  open: boolean;
  onClose: () => void;
  knowledge: DomainKnowledge | null;
  onActionComplete: () => void;
}

const typeIcons: Record<KnowledgeType, React.ReactNode> = {
  terminology: <BookOpen className="h-5 w-5" />,
  specification: <Wrench className="h-5 w-5" />,
  context: <MessageSquare className="h-5 w-5" />,
  expertise_signal: <Award className="h-5 w-5" />,
  validation_rule: <CheckCircle2 className="h-5 w-5" />,
};

const typeLabels: Record<KnowledgeType, string> = {
  terminology: 'Terminology',
  specification: 'Specification',
  context: 'Context',
  expertise_signal: 'Expertise Signal',
  validation_rule: 'Validation Rule',
};

const statusColors: Record<KnowledgeStatus, string> = {
  proposed: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  researching: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  pending_review: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  approved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  active: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
  deprecated: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  resolved: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
};

export function KnowledgeDetails({
  open,
  onClose,
  knowledge,
  onActionComplete,
}: KnowledgeDetailsProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<string | null>(null);
  const [notes, setNotes] = useState('');

  if (!knowledge) return null;

  // Check if this is a category-scoped entry with affected products
  const hasAffectedProducts =
    knowledge.scopeLevel === 'category' &&
    knowledge.affectedProductCount !== undefined &&
    knowledge.affectedProductCount > 0;

  // Navigate to AI Enrichment with pre-filters
  const handleNavigateToEnrichment = () => {
    const params = new URLSearchParams({
      categoryPath: knowledge.scopeCategoryPath || '',
      missingAttribute: knowledge.scopeAttributeName || '',
    });
    navigate(`/enrichment-review?${params.toString()}`);
    onClose();
  };

  const handleStatusChange = async (newStatus: KnowledgeStatus) => {
    setLoading(newStatus);
    try {
      const response = await updateKnowledgeStatus(knowledge.id, newStatus);
      if (response.success) {
        toast({
          title: 'Success',
          description: `Knowledge status updated to ${newStatus}`,
        });
        onActionComplete();
      } else {
        toast({
          title: 'Error',
          description: response.error || 'Failed to update status',
          variant: 'destructive',
        });
      }
    } catch (err) {
      console.error('[KnowledgeDetails] Error updating status:', err);
      toast({
        title: 'Error',
        description: 'Failed to update status',
        variant: 'destructive',
      });
    } finally {
      setLoading(null);
    }
  };

  const handleExecuteResearch = async () => {
    setLoading('research');
    try {
      const response = await executeResearch(knowledge.id);
      if (response.success) {
        toast({
          title: 'Research Started',
          description: 'Research is being executed. Refresh to see results.',
        });
        onActionComplete();
      } else {
        toast({
          title: 'Error',
          description: response.error || 'Failed to start research',
          variant: 'destructive',
        });
      }
    } catch (err) {
      console.error('[KnowledgeDetails] Error executing research:', err);
      toast({
        title: 'Error',
        description: 'Failed to execute research',
        variant: 'destructive',
      });
    } finally {
      setLoading(null);
    }
  };

  const handleRetryResearch = async () => {
    setLoading('retry');
    try {
      const response = await retryResearch(knowledge.id);
      if (response.success) {
        toast({
          title: 'Research Retried',
          description: 'Research is being re-executed.',
        });
        onActionComplete();
      } else {
        toast({
          title: 'Error',
          description: response.error || 'Failed to retry research',
          variant: 'destructive',
        });
      }
    } catch (err) {
      console.error('[KnowledgeDetails] Error retrying research:', err);
      toast({
        title: 'Error',
        description: 'Failed to retry research',
        variant: 'destructive',
      });
    } finally {
      setLoading(null);
    }
  };

  const handleActivate = async () => {
    setLoading('activate');
    try {
      // First activate the knowledge
      const statusResponse = await updateKnowledgeStatus(knowledge.id, 'active');
      if (!statusResponse.success) {
        throw new Error(statusResponse.error || 'Failed to activate');
      }

      // Then propagate staleness
      const stalenessResponse = await propagateStaleness(knowledge.id);

      toast({
        title: 'Knowledge Activated',
        description: stalenessResponse.success
          ? `${stalenessResponse.data?.affectedCount || 0} content items marked for regeneration`
          : 'Activated without staleness propagation',
      });
      onActionComplete();
    } catch (err) {
      console.error('[KnowledgeDetails] Error activating:', err);
      toast({
        title: 'Error',
        description: 'Failed to activate knowledge',
        variant: 'destructive',
      });
    } finally {
      setLoading(null);
    }
  };

  const getScopeDisplay = () => {
    switch (knowledge.scopeLevel) {
      case 'sector':
        return `Sector #${knowledge.scopeSectorId}`;
      case 'category':
        return knowledge.scopeCategoryPath;
      case 'brand':
        return knowledge.scopeBrand;
      case 'attribute':
        return knowledge.scopeAttributeName;
      case 'product':
        return `Product ${knowledge.scopeProductId}`;
      default:
        return 'Unknown';
    }
  };

  const canApprove = ['proposed', 'pending_review'].includes(knowledge.status);
  const canActivate = knowledge.status === 'approved';
  const canDeprecate = ['approved', 'active'].includes(knowledge.status);
  const canResearch = knowledge.researchStatus === 'pending' && knowledge.researchQuestion;
  const canRetryResearch = knowledge.researchStatus === 'failed';

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-muted">
              {typeIcons[knowledge.knowledgeType]}
            </div>
            <div className="flex-1">
              <SheetTitle className="text-lg">{knowledge.title}</SheetTitle>
              <SheetDescription className="flex items-center gap-2 mt-1">
                <Badge className={statusColors[knowledge.status]}>
                  {knowledge.status.replace('_', ' ')}
                </Badge>
                <span className="text-xs">
                  {typeLabels[knowledge.knowledgeType]}
                </span>
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Knowledge Content */}
          <div>
            <Label className="text-xs uppercase text-muted-foreground">
              Knowledge
            </Label>
            <div className="mt-2 p-3 rounded-lg bg-secondary text-secondary-foreground text-sm">
              {knowledge.knowledgeText || (
                <span className="text-muted-foreground italic">
                  No content yet - execute research to populate
                </span>
              )}
            </div>
          </div>

          {/* Scope */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase text-muted-foreground">
                Scope Level
              </Label>
              <p className="mt-1 text-sm font-medium capitalize">
                {knowledge.scopeLevel}
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase text-muted-foreground">
                Scope Target
              </Label>
              <p className="mt-1 text-sm font-medium">{getScopeDisplay()}</p>
            </div>
          </div>

          {/* Research Section */}
          <Accordion type="single" collapsible defaultValue="research">
            <AccordionItem value="research">
              <AccordionTrigger className="text-sm font-medium">
                <div className="flex items-center gap-2">
                  <FlaskConical className="h-4 w-4" />
                  Research Status
                  <Badge variant="outline" className="ml-2">
                    {knowledge.researchStatus}
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="space-y-3 pt-2">
                {knowledge.researchQuestion && (
                  <div>
                    <Label className="text-xs uppercase text-muted-foreground">
                      Research Question
                    </Label>
                    <p className="mt-1 text-sm">{knowledge.researchQuestion}</p>
                  </div>
                )}

                {knowledge.researchMethodology && (
                  <div>
                    <Label className="text-xs uppercase text-muted-foreground">
                      Methodology
                    </Label>
                    <p className="mt-1 text-sm font-mono text-xs">
                      {knowledge.researchMethodology}
                    </p>
                  </div>
                )}

                {knowledge.researchFindings && (
                  <div>
                    <Label className="text-xs uppercase text-muted-foreground">
                      Findings
                    </Label>
                    <div className="mt-1 p-3 rounded bg-secondary max-h-96 overflow-y-auto prose prose-sm dark:prose-invert max-w-none">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          h1: ({ children }) => <h1 className="text-base font-bold mt-3 mb-2">{children}</h1>,
                          h2: ({ children }) => <h2 className="text-sm font-bold mt-3 mb-2">{children}</h2>,
                          h3: ({ children }) => <h3 className="text-sm font-semibold mt-2 mb-1">{children}</h3>,
                          h4: ({ children }) => <h4 className="text-xs font-semibold mt-2 mb-1">{children}</h4>,
                          p: ({ children }) => <p className="text-xs mb-2 leading-relaxed">{children}</p>,
                          ul: ({ children }) => <ul className="text-xs list-disc pl-4 mb-2 space-y-1">{children}</ul>,
                          ol: ({ children }) => <ol className="text-xs list-decimal pl-4 mb-2 space-y-1">{children}</ol>,
                          li: ({ children }) => <li className="text-xs">{children}</li>,
                          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                          em: ({ children }) => <em className="italic">{children}</em>,
                          code: ({ children }) => <code className="bg-muted px-1 rounded text-xs">{children}</code>,
                        }}
                      >
                        {knowledge.researchFindings}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}

                {knowledge.researchSources && knowledge.researchSources.length > 0 && (
                  <div>
                    <Label className="text-xs uppercase text-muted-foreground">
                      Sources ({knowledge.researchSources.length})
                    </Label>
                    <ul className="mt-1 space-y-1">
                      {knowledge.researchSources.slice(0, 5).map((source: any, idx: number) => (
                        <li key={idx} className="text-xs flex items-center gap-1">
                          <ExternalLink className="h-3 w-3" />
                          {typeof source === 'string' ? source : source.title || source.url}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  {canResearch && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleExecuteResearch}
                      disabled={loading !== null}
                    >
                      {loading === 'research' ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Play className="h-4 w-4 mr-1" />
                      )}
                      Execute Research
                    </Button>
                  )}
                  {canRetryResearch && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleRetryResearch}
                      disabled={loading !== null}
                    >
                      {loading === 'retry' ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <RotateCcw className="h-4 w-4 mr-1" />
                      )}
                      Retry Research
                    </Button>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Gap Analysis */}
            {knowledge.gap_analysis && (
              <AccordionItem value="gap">
                <AccordionTrigger className="text-sm font-medium">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Gap Analysis
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-2 pt-2">
                  {knowledge.gap_analysis.ROOT_CAUSE && (
                    <div>
                      <Label className="text-xs uppercase text-muted-foreground">
                        Root Cause
                      </Label>
                      <p className="mt-1 text-sm">{knowledge.gap_analysis.ROOT_CAUSE}</p>
                    </div>
                  )}
                  {knowledge.gap_analysis.KNOWLEDGE_GAP && (
                    <div>
                      <Label className="text-xs uppercase text-muted-foreground">
                        Knowledge Gap
                      </Label>
                      <p className="mt-1 text-sm">{knowledge.gap_analysis.KNOWLEDGE_GAP}</p>
                    </div>
                  )}
                  {knowledge.gap_analysis.SUGGESTED_CORRECTION && (
                    <div>
                      <Label className="text-xs uppercase text-muted-foreground">
                        Suggested Correction
                      </Label>
                      <p className="mt-1 text-sm">{knowledge.gap_analysis.SUGGESTED_CORRECTION}</p>
                    </div>
                  )}
                  {knowledge.gap_analysis.CONFIDENCE && (
                    <Badge variant="outline">
                      Confidence: {knowledge.gap_analysis.CONFIDENCE}
                    </Badge>
                  )}
                </AccordionContent>
              </AccordionItem>
            )}

            {/* Affected Products - for category-scoped specification gaps */}
            {hasAffectedProducts && (
              <AccordionItem value="affected">
                <AccordionTrigger className="text-sm font-medium">
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    Affected Products
                    <Badge
                      variant="outline"
                      className="ml-2 bg-orange-50 text-orange-700 border-orange-200"
                    >
                      {knowledge.affectedProductCount}
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-3 pt-2">
                  <div className="p-3 rounded-lg bg-orange-50 border border-orange-200 dark:bg-orange-900/20 dark:border-orange-800">
                    <p className="text-sm text-orange-800 dark:text-orange-200">
                      {knowledge.affectedProductCount} product{knowledge.affectedProductCount !== 1 ? 's' : ''} in{' '}
                      <span className="font-medium">{knowledge.scopeCategoryPath}</span>{' '}
                      {knowledge.scopeAttributeName && (
                        <>
                          {knowledge.affectedProductCount !== 1 ? 'are' : 'is'} missing{' '}
                          <span className="font-medium">{knowledge.scopeAttributeName}</span>
                        </>
                      )}
                    </p>
                  </div>

                  <div>
                    <Label className="text-xs uppercase text-muted-foreground">
                      Resolution Path
                    </Label>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Use AI Enrichment to research and fill in the missing specification
                      for each affected product.
                    </p>
                  </div>

                  <Button
                    className="w-full"
                    onClick={handleNavigateToEnrichment}
                  >
                    <Wrench className="h-4 w-4 mr-2" />
                    Research in AI Enrichment
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </AccordionContent>
              </AccordionItem>
            )}

            {/* Metadata */}
            <AccordionItem value="meta">
              <AccordionTrigger className="text-sm font-medium">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Metadata
                </div>
              </AccordionTrigger>
              <AccordionContent className="space-y-2 pt-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created</span>
                  <span>{knowledge.createdAt ? format(new Date(knowledge.createdAt), 'PPpp') : '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Updated</span>
                  <span>{knowledge.updatedAt ? format(new Date(knowledge.updatedAt), 'PPpp') : '-'}</span>
                </div>
                {knowledge.approvedAt && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Approved</span>
                    <span>{format(new Date(knowledge.approvedAt), 'PPpp')}</span>
                  </div>
                )}
                {knowledge.effectivenessScore !== null && knowledge.effectivenessScore !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Effectiveness</span>
                    <span>{(knowledge.effectivenessScore * 100).toFixed(0)}%</span>
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <Separator />

          {/* Actions */}
          <div className="space-y-3">
            <Label className="text-xs uppercase text-muted-foreground">
              Actions
            </Label>

            <div className="flex flex-wrap gap-2">
              {canApprove && (
                <Button
                  size="sm"
                  onClick={() => handleStatusChange('approved')}
                  disabled={loading !== null}
                >
                  {loading === 'approved' ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <ThumbsUp className="h-4 w-4 mr-1" />
                  )}
                  Approve
                </Button>
              )}

              {canActivate && (
                <Button
                  size="sm"
                  variant="default"
                  onClick={handleActivate}
                  disabled={loading !== null}
                >
                  {loading === 'activate' ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Zap className="h-4 w-4 mr-1" />
                  )}
                  Activate & Propagate
                </Button>
              )}

              {canDeprecate && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleStatusChange('deprecated')}
                  disabled={loading !== null}
                >
                  {loading === 'deprecated' ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <ThumbsDown className="h-4 w-4 mr-1" />
                  )}
                  Deprecate
                </Button>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
